import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import DashboardLayout from '../components/DashboardLayout';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { CheckCircle, ArrowRight, Zap, CreditCard, Star, XCircle, AlertTriangle, Shield, Loader2, Receipt } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = 'https://cyberguard-api-ka8f.onrender.com';

const plans = [
  {
    id: 'starter',
    name: 'Starter',
    price: 29,
    icon: Shield,
    color: 'blue',
    features: [
      '5 audits par mois',
      'Scan SSL/TLS & certificat',
      'Redirection HTTP → HTTPS',
      'Headers basiques (HSTS, CSP, X-Frame)',
      'Emails : SPF, DKIM, DMARC',
      'Mentions légales et bandeau cookies',
      'Note de A à G',
    ],
  },
  {
    id: 'pro',
    name: 'Pro',
    price: 79,
    icon: Zap,
    color: 'green',
    popular: true,
    features: [
      '30 audits par mois',
      'Tout le plan Starter inclus',
      'Headers avancés (X-Content-Type, Referrer, Permissions)',
      'Analyse CORS & Cookies',
      'Détection version serveur',
      'Rapport IA et guide de correction par point',
      'Analyse approfondie sur domaine vérifié',
      'Surveillance hebdomadaire (10 domaines)',
    ],
  },
  {
    id: 'enterprise',
    name: 'Enterprise',
    price: 199,
    icon: CreditCard,
    color: 'purple',
    features: [
      'Audits illimités',
      'Tout le plan Pro inclus',
      'Marque blanche : rapports PDF et liens de partage à votre logo',
      'Fiches clients illimitées',
      'Surveillance hebdomadaire (50 domaines)',
      'Accès API REST',
      'Support prioritaire',
    ],
  },
];

const colorMap = {
  blue:   { border: 'border-blue-500/30',   bg: 'bg-blue-500/10',   text: 'text-blue-400',   btn: 'bg-blue-600 hover:bg-blue-700' },
  green:  { border: 'border-green-500',     bg: 'bg-green-500/10',  text: 'text-green-400',  btn: 'bg-green-600 hover:bg-green-700' },
  purple: { border: 'border-purple-500/30', bg: 'bg-purple-500/10', text: 'text-purple-400', btn: 'bg-purple-600 hover:bg-purple-700' },
};

export default function Billing() {
  const { user, token, updateUser } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(null);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [cancelling, setCancelling] = useState(false);

  const plan = user?.subscription_plan || 'free';
  const isPaid = plan !== 'free';
  const hasSubscription = isPaid && !!user?.stripe_subscription_id;
  const cancellationScheduled = !!user?.cancellation_scheduled;
  const [openingPortal, setOpeningPortal] = useState(false);

  const openPortal = async () => {
    setOpeningPortal(true);
    try {
      const res = await axios.post(`${API_URL}/api/stripe/portal`, {}, { headers: { Authorization: `Bearer ${token}` } });
      window.location.href = res.data.url;
    } catch (err) {
      toast.error(err.response?.data?.detail || "Impossible d'ouvrir l'espace abonnement");
      setOpeningPortal(false);
    }
  };

  const changePlan = async (p) => {
    const order = { free: 0, starter: 1, pro: 2, enterprise: 3 };
    const up = order[p.id] > order[plan];
    const msg = up
      ? `Passer au plan ${p.name} (${p.price} €/mois) ? Les nouvelles fonctionnalités sont disponibles tout de suite ; la différence pour le mois en cours est ajoutée, au prorata, à votre prochaine facture.`
      : `Passer au plan ${p.name} (${p.price} €/mois) ? Le changement est immédiat et la différence vous est créditée, au prorata, sur votre prochaine facture.`;
    if (!window.confirm(msg)) return;
    setLoading(p.id);
    try {
      await axios.post(`${API_URL}/api/stripe/change-plan`, { plan: p.id }, { headers: { Authorization: `Bearer ${token}` } });
      updateUser({ subscription_plan: p.id, cancellation_scheduled: false });
      toast.success(`Vous êtes maintenant sur le plan ${p.name}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Le changement de plan a échoué');
    } finally {
      setLoading(null);
    }
  };

  // Gestion retour depuis Stripe
  useEffect(() => {
    if (searchParams.get('success') === 'true') {
      const newPlan = searchParams.get('plan');
      toast.success(`🎉 Abonnement ${newPlan} activé avec succès !`);
      if (newPlan) updateUser({ subscription_plan: newPlan });
      navigate('/billing', { replace: true });
    }
    if (searchParams.get('cancelled') === 'true') {
      toast.info('Paiement annulé.');
      navigate('/billing', { replace: true });
    }
  }, [searchParams]);

  const handleSubscribe = async (p) => {
    if (p.id === plan) { toast.info('Vous avez déjà ce plan'); return; }
    if (hasSubscription) { changePlan(p); return; }
    setLoading(p.id);
    try {
      const res = await axios.post(
        `${API_URL}/api/stripe/create-checkout-session`,
        { plan: p.id },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      // Redirige vers Stripe Checkout
      window.location.href = res.data.url;
    } catch (err) {
      toast.error('Erreur lors de la création de la session de paiement.');
      setLoading(null);
    }
  };

  const handleCancelSubscription = async () => {
    setCancelling(true);
    try {
      await axios.post(
        `${API_URL}/api/stripe/cancel`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Abonnement annulé. Votre accès reste actif jusqu\'à la fin de la période payée.');
      updateUser({ cancellation_scheduled: true });
      setShowCancelConfirm(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur lors de l\'annulation.');
    } finally {
      setCancelling(false);
    }
  };

  const getButtonLabel = (p) => {
    if (p.id === plan) return 'Plan actuel';
    const order = { free: 0, starter: 1, pro: 2, enterprise: 3 };
    return order[p.id] > order[plan] ? `Passer à ${p.name}` : 'Downgrade';
  };

  return (
    <DashboardLayout>
      <div className="space-y-8">

        {/* Header */}
        <div>
          <h1 className="text-2xl font-bold text-white font-mono uppercase tracking-tighter">Facturation</h1>
          <p className="text-zinc-500 text-xs font-mono mt-1 uppercase tracking-widest">Gérez votre abonnement CyberGuard</p>
        </div>

        {/* Plan actuel */}
        <div className="bg-zinc-900 border border-zinc-800 rounded-sm p-6">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div>
              <p className="text-zinc-500 text-[10px] font-mono uppercase tracking-widest">Plan actuel</p>
              <h2 className="text-xl font-black text-white font-mono uppercase mt-1">
                {plan === 'free' ? 'Aucun plan actif' : plan}
              </h2>
              <p className="text-zinc-600 text-[10px] font-mono mt-1">
                {plan === 'free'       ? 'Choisissez un plan pour commencer' :
                 plan === 'starter'    ? '5 audits/mois · Scan basique' :
                 plan === 'pro'        ? '30 audits/mois · Rapports IA · Scan complet' :
                 'Audits illimités · Tout inclus · Support 24/7'}
              </p>
            </div>
            <div className="flex items-center gap-3">
              <div className={`px-4 py-2 border rounded-sm text-xs font-mono uppercase font-bold ${
                plan === 'enterprise' ? 'border-purple-500/30 text-purple-400 bg-purple-500/10' :
                plan === 'pro'        ? 'border-green-500/30 text-green-400 bg-green-500/10' :
                plan === 'starter'    ? 'border-blue-500/30 text-blue-400 bg-blue-500/10' :
                                       'border-zinc-700 text-zinc-400 bg-zinc-800'
              }`}>
                {plan === 'free' ? 'Aucun plan' : cancellationScheduled ? 'Résiliation programmée' : 'Actif ✓'}
              </div>
              {user?.stripe_customer_id && (
                <button
                  onClick={openPortal}
                  disabled={openingPortal}
                  data-testid="billing-portal"
                  className="flex items-center gap-2 px-3 py-2 border border-zinc-700 text-zinc-200 hover:border-blue-500 font-mono text-[10px] uppercase transition-all rounded-sm disabled:opacity-50"
                >
                  {openingPortal ? <Loader2 className="w-3 h-3 animate-spin" /> : <Receipt className="w-3 h-3" />} Factures et carte bancaire
                </button>
              )}
              {isPaid && !cancellationScheduled && (
                <button
                  onClick={() => setShowCancelConfirm(true)}
                  className="flex items-center gap-2 px-3 py-2 border border-red-500/30 text-red-400 hover:bg-red-500/10 font-mono text-[10px] uppercase transition-all rounded-sm"
                >
                  <XCircle className="w-3 h-3" /> Annuler l'abonnement
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Modal confirmation annulation */}
        {showCancelConfirm && (
          <div className="bg-red-500/5 border border-red-500/30 rounded-sm p-6 space-y-4">
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-red-500 shrink-0 mt-0.5" />
              <div>
                <p className="text-white font-mono font-bold text-sm uppercase">Confirmer l'annulation</p>
                <p className="text-zinc-500 font-mono text-xs mt-1">
                  Votre accès {plan} restera actif jusqu'à la fin de la période payée, puis sera automatiquement résilié.
                </p>
              </div>
            </div>
            <div className="flex gap-3">
              <button
                onClick={handleCancelSubscription}
                disabled={cancelling}
                className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white font-mono text-xs uppercase rounded-sm transition-all disabled:opacity-50"
              >
                {cancelling ? 'Annulation...' : 'Confirmer l\'annulation'}
              </button>
              <button
                onClick={() => setShowCancelConfirm(false)}
                className="px-4 py-2 border border-zinc-700 text-zinc-400 hover:text-white font-mono text-xs uppercase rounded-sm transition-all"
              >
                Garder mon abonnement
              </button>
            </div>
          </div>
        )}

        {/* Plans */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {plans.map((p) => {
            const isCurrentPlan = p.id === plan;
            const c = colorMap[p.color];
            const Icon = p.icon;
            return (
              <div key={p.id} className={`relative bg-zinc-900 border rounded-sm p-6 transition-all ${
                p.popular ? 'border-green-500 shadow-[0_0_30px_rgba(34,197,94,0.1)]' :
                isCurrentPlan ? `${c.border} opacity-90` : 'border-zinc-800 hover:border-zinc-700'
              }`}>
                {p.popular && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                    <span className="bg-green-500 text-black text-[10px] font-black font-mono px-3 py-1 uppercase tracking-widest flex items-center gap-1">
                      <Star className="w-3 h-3" /> Recommandé
                    </span>
                  </div>
                )}

                <div className="flex items-center gap-3 mb-5">
                  <div className={`w-10 h-10 rounded-sm flex items-center justify-center border ${c.border} ${c.bg}`}>
                    <Icon className={`w-5 h-5 ${c.text}`} />
                  </div>
                  <h3 className="text-lg font-black text-white font-mono uppercase">{p.name}</h3>
                </div>

                <div className="mb-6">
                  <span className={`text-5xl font-black font-mono ${c.text}`}>{p.price}€</span>
                  <span className="text-zinc-500 font-mono text-sm">/mois</span>
                </div>

                <ul className="space-y-2 mb-6">
                  {p.features.map((f, i) => (
                    <li key={i} className="flex items-start gap-2 text-xs font-mono text-zinc-400">
                      <CheckCircle className={`w-4 h-4 shrink-0 mt-0.5 ${c.text}`} /> {f}
                    </li>
                  ))}
                </ul>

                <Button
                  onClick={() => handleSubscribe(p)}
                  disabled={!!loading || isCurrentPlan}
                  className={`w-full font-mono rounded-none text-xs font-bold uppercase ${
                    isCurrentPlan ? 'bg-zinc-800 text-zinc-500 cursor-not-allowed' : c.btn
                  }`}
                >
                  {loading === p.id ? (
                    <><Loader2 className="w-3 h-3 mr-2 animate-spin" /> Redirection...</>
                  ) : isCurrentPlan ? (
                    <><CheckCircle className="w-3 h-3 mr-2" /> Plan actuel</>
                  ) : (
                    <>{getButtonLabel(p)} <ArrowRight className="w-3 h-3 ml-2" /></>
                  )}
                </Button>
              </div>
            );
          })}
        </div>

        {/* FAQ */}
        <div className="bg-zinc-900 border border-zinc-800 rounded-sm p-6 space-y-4">
          <h3 className="text-white font-mono font-bold text-xs uppercase tracking-widest border-l-2 border-blue-500 pl-3">FAQ</h3>
          {[
            { q: 'Puis-je changer de plan ?', a: 'Oui, à tout moment depuis cette page. Le changement est immédiat et la différence est calculée au prorata sur votre prochaine facture.' },
            { q: 'Où trouver mes factures ?', a: 'Bouton « Factures et carte bancaire » ci-dessus : vous y téléchargez toutes vos factures et vous pouvez changer de carte bancaire.' },
            { q: 'Que se passe-t-il si j\'annule ?', a: 'Vous gardez l\'accès jusqu\'à la fin de la période payée, puis votre compte repasse en accès gratuit. Vos audits restent consultables. Vous pouvez revenir sur la résiliation avant cette date depuis « Factures et carte bancaire ».' },
            { q: 'Quels moyens de paiement acceptez-vous ?', a: 'Carte bancaire (Visa, Mastercard, American Express) via Stripe. Paiement 100% sécurisé.' },
            { q: 'Les rapports IA sont-ils inclus dans le plan Starter ?', a: 'Non, les rapports IA sont réservés aux plans Pro et Enterprise.' },
          ].map(({ q, a }) => (
            <div key={q} className="border-b border-zinc-800 pb-4 last:border-0 last:pb-0">
              <h4 className="text-zinc-300 font-mono text-xs font-bold mb-1 uppercase">{q}</h4>
              <p className="text-zinc-500 font-mono text-[10px] leading-relaxed">{a}</p>
            </div>
          ))}
        </div>

      </div>
    </DashboardLayout>
  );
}
